/**
 * Email ingest: the address to give a vendor, and who is allowed to use it.
 *
 * THE ADDRESS IS THE PRODUCT HERE. Everything else on this screen is
 * plumbing - the thing a technician actually needs is one line they can paste
 * into a backup appliance's notification settings. So it is first, it is
 * large, and it copies with one click.
 *
 * THE QUARANTINE IS NOT AN ERROR LIST. The first email from every new
 * appliance lands there by design, and the expected action is "yes, that one" -
 * so it reads as a queue to work rather than a wall of failures.
 */

import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { errText, ago } from '../btApi';

function call<TIn extends object, TOut>(name: string) {
  return async (data: TIn): Promise<TOut> => (await httpsCallable<TIn, TOut>(functions, name)(data)).data;
}

interface Settings { address: string; enabled: boolean; allowedSenders: string[] }

interface Message {
  id: string;
  from: string;
  subject: string;
  verdict: string;
  verdictReason: string;
  vendorLabel: string;
  parsed: boolean;
  unrecognised: boolean;
  rowCount: number;
  rows: Array<{ name: string; client: string; status: string; reason: string }>;
  error: string | null;
  receivedAtMs: number;
}

const getSettings = call<{ orgId: string }, Settings>('getIngestSettings');
const setSettings = call<{ orgId: string; enabled?: boolean; allowedSenders?: string[] }, { ok: boolean }>('setIngestSettings');
const listInbound = call<{ orgId: string; verdict?: string }, { messages: Message[] }>('listInbound');
const approveSender = call<{ orgId: string; messageId: string }, { ok: boolean; domain: string }>('approveInboundSender');
const revokeSender = call<{ orgId: string; domain: string }, { ok: boolean }>('revokeInboundSender');

export function EmailIngest({ orgId, canManage }: { orgId: string; canManage: boolean }) {
  const [s, setS] = useState<Settings | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      const [a, b] = await Promise.all([getSettings({ orgId }), listInbound({ orgId })]);
      setS(a); setMessages(b.messages);
    } catch (e) { setErr(errText(e)); }
  };
  useEffect(() => { if (open) void load(); }, [orgId, open]);

  async function act(key: string, fn: () => Promise<unknown>) {
    setBusy(key); setErr(null); setMsg(null);
    try { await fn(); await load(); }
    catch (e) { setErr(errText(e)); }
    finally { setBusy(null); }
  }

  const quarantined = messages.filter((m) => m.verdict === 'quarantine');
  const accepted = messages.filter((m) => m.verdict === 'accept');

  return (
    <section className="panel" style={{ marginTop: '1rem' }}>
      <div className="integ-head">
        <div>
          <h3 className="panel-title" style={{ margin: 0 }}>Backup emails</h3>
          <div className="dim" style={{ fontSize: 12 }}>
            For the products with no API &mdash; point their report emails here and they land on the board.
          </div>
        </div>
        <button className="btn-link" onClick={() => setOpen(!open)}>
          {open ? 'hide' : quarantined.length ? `show (${quarantined.length} waiting)` : 'show'}
        </button>
      </div>

      {open && (
        <>
          {err && <div className="banner banner-error">{err}</div>}
          {msg && <div className="banner">{msg}</div>}
          {!s ? <div className="empty">Loading…</div> : (
            <>
              {/* The one line somebody pastes into an appliance. */}
              <div className="ingest-addr">
                <div className="field-label">Send backup reports to</div>
                <div className="ingest-row">
                  <code className="ingest-value">{s.address}</code>
                  <button
                    className="btn"
                    onClick={() => {
                      void navigator.clipboard?.writeText(s.address);
                      setCopied(true);
                      window.setTimeout(() => setCopied(false), 1500);
                    }}
                  >
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
                <div className="field-hint">
                  Unique to this organisation. Anything sent here is kept, but only mail from an
                  approved sender reaches the board.
                </div>
              </div>

              {canManage && (
                <label className="check-row" style={{ marginTop: 10 }}>
                  <input
                    type="checkbox" checked={s.enabled}
                    onChange={(e) => void act('enable', () => setSettings({ orgId, enabled: e.target.checked }))}
                  />
                  <span>Accept backup reports by email</span>
                </label>
              )}

              {/* --- the queue --------------------------------------------- */}
              <h4 className="panel-title" style={{ fontSize: 13, marginTop: 18 }}>
                Waiting for approval ({quarantined.length})
              </h4>
              {quarantined.length === 0 ? (
                <div className="dim" style={{ fontSize: 12 }}>
                  Nothing waiting. The first email from any new appliance appears here until you approve it.
                </div>
              ) : (
                <table className="mini-table">
                  <thead><tr><th>From</th><th>Subject</th><th>Received</th><th>Why held</th><th /></tr></thead>
                  <tbody>
                    {quarantined.map((m) => (
                      <tr key={m.id}>
                        <td className="mono" style={{ fontSize: 11 }}>{m.from}</td>
                        <td style={{ fontSize: 12 }}>{m.subject || <span className="dim">no subject</span>}</td>
                        <td className="dim" style={{ fontSize: 11 }}>
                          {m.receivedAtMs ? ago(new Date(m.receivedAtMs).toISOString()) : ''}
                        </td>
                        <td className="dim" style={{ fontSize: 11 }}>{m.verdictReason}</td>
                        <td style={{ textAlign: 'right' }}>
                          {canManage && (
                            <button
                              className="btn" disabled={busy !== null}
                              title="Approve this sender's domain and start collecting from it"
                              onClick={() => void act('ok' + m.id, async () => {
                                const r = await approveSender({ orgId, messageId: m.id });
                                setMsg(`Approved ${r.domain}. Future reports from that domain reach the board.`);
                              })}
                            >
                              {busy === 'ok' + m.id ? 'Approving…' : 'Approve sender'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* --- approved senders -------------------------------------- */}
              <h4 className="panel-title" style={{ fontSize: 13, marginTop: 18 }}>Approved senders</h4>
              {s.allowedSenders.length === 0 ? (
                <div className="dim" style={{ fontSize: 12 }}>
                  None yet. Until one is approved, everything that arrives is held &mdash; which is the
                  point: an address nobody has vouched for cannot write to your board.
                </div>
              ) : (
                <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                  {s.allowedSenders.map((d) => (
                    <span key={d} className="tag tag-ok">
                      {d}
                      {canManage && (
                        <button
                          className="btn-link danger" style={{ marginLeft: 6 }} disabled={busy !== null}
                          onClick={() => void act('rm' + d, () => revokeSender({ orgId, domain: d }))}
                        >
                          remove
                        </button>
                      )}
                    </span>
                  ))}
                </div>
              )}

              {/* --- what has been read ------------------------------------ */}
              {accepted.length > 0 && (
                <>
                  <h4 className="panel-title" style={{ fontSize: 13, marginTop: 18 }}>Recently read</h4>
                  <table className="mini-table">
                    <thead><tr><th>From</th><th>Subject</th><th>Read as</th><th>Machines</th><th>When</th></tr></thead>
                    <tbody>
                      {accepted.slice(0, 12).map((m) => (
                        <tr key={m.id}>
                          <td className="mono" style={{ fontSize: 11 }}>{m.from}</td>
                          <td style={{ fontSize: 12 }}>{m.subject}</td>
                          <td style={{ fontSize: 11 }}>
                            {m.unrecognised
                              ? <span className="tag tag-warn">could not read</span>
                              : <span className="tag">{m.vendorLabel}</span>}
                          </td>
                          <td className="dim" style={{ fontSize: 11 }}>
                            {m.rowCount === 0
                              ? <span className="dim">none found</span>
                              : m.rows.slice(0, 4).map((r) => r.name).join(', ') +
                                (m.rowCount > 4 ? ` +${m.rowCount - 4}` : '')}
                          </td>
                          <td className="dim" style={{ fontSize: 11 }}>
                            {m.receivedAtMs ? ago(new Date(m.receivedAtMs).toISOString()) : ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {/* Said plainly: an email we could not read is a gap in the
                      monitoring, not a cosmetic problem. */}
                  {accepted.some((m) => m.unrecognised) && (
                    <div className="banner banner-warn" style={{ marginTop: 8 }}>
                      Some emails arrived that the parser could make nothing of. Those machines are not
                      being monitored &mdash; send one over and the format can be added.
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}
    </section>
  );
}
